import { AuthState, State } from "../../../components/Workbench/types";
import { ApiErrorResponse } from "../types/common";

export enum ApiMethod {
  Delete = "DELETE",
  Get = "GET",
  Post = "POST",
  Put = "PUT",
}

export enum SupportedContentType {
  Binary = "application/octet-stream",
  Json = "application/json",
  Png = "image/png",
  Text = "text/plain",
  Xml = "application/xml",
}

interface FetchOptions {
  body?: string;
  credentials: "omit";
  headers: Headers;
  method: ApiMethod;
}

export interface Params {
  [key: string]: string;
}

export type RequestResponseType<Type extends SupportedContentType, T> = Type extends
  | SupportedContentType.Binary
  | SupportedContentType.Png
  ? Blob
  : Type extends SupportedContentType.Text | SupportedContentType.Xml
  ? string
  : Type extends SupportedContentType.Json
  ? T
  : never;

export abstract class AbstractBaseApi {
  public static getApiBaseUri(): string {
    if (typeof (window as any).API_BASE_URI === "string") {
      return (window as any).API_BASE_URI;
    }
    return AbstractBaseApi.API_BASE_URI;
  }
  private static API_BASE_URI = "https://api.innodata.com/v1.1";

  private auth: State["auth"];
  public constructor(auth: State["auth"]) {
    this.auth = auth;
  }

  protected async makeApiRequest<ResponseContentType extends SupportedContentType, T>(
    method: ApiMethod,
    endPoint: string,
    requiresAuth: boolean,
    responseContentType: ResponseContentType,
    queryParams?: Params,
    customHeaders?: Params,
    postBody?: string,
    requestContentType?: SupportedContentType,
  ): Promise<RequestResponseType<ResponseContentType, T>> {
    const uri = this.buildUri(endPoint, queryParams || {});
    const options = this.buildOptions(
      endPoint,
      method,
      customHeaders || {},
      postBody || null,
      requestContentType || SupportedContentType.Json,
      requiresAuth,
    );
    const response = await window.fetch(uri, options);
    // handle non 200 responses
    if (!response.ok) {
      if (response.headers.get("content-type")?.startsWith(SupportedContentType.Json)) {
        let errorResponse;
        try {
          errorResponse = await response.json();
          if (!errorResponse || !errorResponse.response) {
            throw new Error();
          }
        } catch (err) {
          throw new Error("Unexpected error with API call, response is not JSON.");
        }
        let responseObject: ApiErrorResponse<any> = errorResponse.response;
        if (
          typeof errorResponse.response === "object" &&
          errorResponse.response !== null &&
          !Array.isArray(errorResponse.response)
        ) {
          responseObject = errorResponse.response;
        }
        const error = new Error(responseObject.message);
        (error as any).source = responseObject;
        throw error;
      }
      throw new Error("Unexpected error with API call, response is not JSON.");
    }
    switch (responseContentType) {
      case SupportedContentType.Binary:
      case SupportedContentType.Png:
        return response.blob() as any;
      case SupportedContentType.Text:
      case SupportedContentType.Xml:
        return response.text() as any;
      case SupportedContentType.Json:
      default:
        const toReturn = await response.json();

        return toReturn.response;
    }
  }

  private buildUri(endPoint: string, queryParams: Params): string {
    const uri = `${AbstractBaseApi.getApiBaseUri()}${endPoint}`;
    if (Object.keys(queryParams).length < 1) {
      return uri;
    }
    const query = Object.keys(queryParams)
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
      .join("&");
    return `${uri}?${query}`;
  }

  private buildOptions(
    endPoint: string,
    method: ApiMethod,
    customHeaders: Params,
    postBody: string | null,
    requestContentType: SupportedContentType,
    requiresAuth: boolean,
  ): FetchOptions {
    const headers: Headers = new Headers(customHeaders);
    if (requiresAuth) {
      switch (this.auth.state) {
        case AuthState.initial:
        case AuthState.loggingInByForm:
        case AuthState.notLoggedIn:
          throw new Error(`Endpoint: ${endPoint} requires authentication.`);
      }
      headers.set("Authorization", `Basic ${this.auth.authString}`);
    }
    headers.set("Content-type", requestContentType);
    const options: FetchOptions = {
      credentials: "omit",
      headers,
      method,
    };
    if (method !== ApiMethod.Get && postBody !== null && postBody.length > 0) {
      options.body = postBody;
    }
    return options;
  }
}
